import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Key,
  ContentCopy,
  CheckCircle,
  Info,
  Security,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const ApiTokenPage: React.FC = () => {
  const { t } = useTranslation();
  const [generatedToken, setGeneratedToken] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [showTokenDialog, setShowTokenDialog] = useState(false);

  const handleGenerateApiToken = async () => {
    try {
      setTokenLoading(true);
      const response = await api.post('/generate-api-token');
      setGeneratedToken(response.data.token);
      setShowTokenDialog(true);
    } catch (error) {
      console.error('Error generating API token:', error);
      alert('Failed to generate API token. Please try again.');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(generatedToken);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const handleCloseTokenDialog = () => {
    setShowTokenDialog(false);
    setGeneratedToken('');
    setTokenCopied(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
          {t('apiToken.title')}
        </Typography>

      {/* Information Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Info sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">{t('apiToken.whatAreTokens')}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('apiToken.tokenDescription')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {t('apiToken.tokenFeatures.validity')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {t('apiToken.tokenFeatures.permissions')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {t('apiToken.tokenFeatures.security')}
          </Typography>
        </CardContent>
      </Card>

      {/* Generate Token Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Security sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6">{t('apiToken.generateNewToken')}</Typography>
          </Box>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('apiToken.generatingWarning')}
          </Alert>

          <Button
            variant="contained"
            size="large"
            startIcon={tokenLoading ? <CircularProgress size={20} color="inherit" /> : <Key />}
            onClick={handleGenerateApiToken}
            disabled={tokenLoading}
            sx={{
              background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
              boxShadow: '0 3px 5px 2px rgba(255, 152, 0, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
              },
            }}
          >
            {t('apiToken.generateButton')}
          </Button>
        </CardContent>
      </Card>

      {/* API Documentation Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>{t('apiToken.quickStartGuide')}</Typography>
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            1. Test your connection:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              curl -H "Authorization: Bearer YOUR_TOKEN" \<br />
              &nbsp;&nbsp;https://api-clubs.hikeandfly.app/api/v1/external/test-auth
            </Typography>
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            2. Get current registration year:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              curl -H "Authorization: Bearer YOUR_TOKEN" \<br />
              &nbsp;&nbsp;https://api-clubs.hikeandfly.app/api/v1/external/registration-year
            </Typography>
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            3. Get your clubs:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              curl -H "Authorization: Bearer YOUR_TOKEN" \<br />
              &nbsp;&nbsp;https://api-clubs.hikeandfly.app/api/v1/external/my-clubs
            </Typography>
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            4. Get all pilots from a specific club:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              curl -H "Authorization: Bearer YOUR_TOKEN" \<br />
              &nbsp;&nbsp;"https://api-clubs.hikeandfly.app/api/v1/external/clubs/CLUB_ID/pilots"
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Returns all pilot information including personal details, license info, and medical expiry dates.
            Add ?active_only=false to include inactive pilots.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            5. Get pilot count for a club:
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              curl -H "Authorization: Bearer YOUR_TOKEN" \<br />
              &nbsp;&nbsp;"https://api-clubs.hikeandfly.app/api/v1/external/clubs/CLUB_ID/pilots/count"
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Token Generation Dialog */}
      <Dialog open={showTokenDialog} onClose={handleCloseTokenDialog} maxWidth="md" fullWidth>
        <DialogTitle>API Token Generated Successfully</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Important:</strong> This token will only be shown once. Please copy it now and store it securely.
            The token is valid for 365 days.
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            Your API Token:
          </Typography>
          
          <Paper 
            sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              wordBreak: 'break-all',
              position: 'relative'
            }}
          >
            {generatedToken}
            <IconButton
              onClick={handleCopyToken}
              sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8,
                backgroundColor: tokenCopied ? '#4caf50' : 'transparent',
                color: tokenCopied ? 'white' : 'inherit',
                '&:hover': {
                  backgroundColor: tokenCopied ? '#45a049' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {tokenCopied ? <CheckCircle /> : <ContentCopy />}
            </IconButton>
          </Paper>
          
          {tokenCopied && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Token copied to clipboard!
            </Typography>
          )}
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Next Steps:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              1. Store this token securely in your external system's configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              2. Never share this token or commit it to version control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              3. Use the token in the Authorization header for all API requests
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTokenDialog} variant="contained">
            I've Copied the Token
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApiTokenPage;